"use client";

import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import * as React from "react";

import type { RepaymentStructure } from "@/lib/pricing-calculator/model";
import {
  feePerMonthPercent,
  monthsToRepay,
  simulate,
} from "@/lib/pricing-calculator/model";
import { cn } from "@/lib/utils";

import { BalanceChart } from "./balance-chart";
import { CapComparison } from "./cap-comparison";
import { CalculatorProvider } from "./context";
import { InputsPanel } from "./inputs-panel";
import { MobileSummaryBar } from "./mobile-summary-bar";
import { ResultsSummary } from "./results-summary";
import { useCalculatorState } from "./use-calculator-state";

export interface PricingCalculatorProps {
  /** Request locale for number and date formatting. */
  locale: string;
  /** ISO currency code for the request locale; the currency selector's preset. */
  defaultCurrency: string;
  /** Structure to start on when the URL doesn't set one (default: term loan). */
  defaultStructure?: RepaymentStructure;
  /**
   * Inside a narrow content column (e.g. an article): inputs stack above the
   * results at every width, and the pinned mobile total is left out so it
   * doesn't follow the reader through the rest of the page.
   */
  embedded?: boolean;
  className?: string;
}

const ANALYTICS_DELAY_MS = 800;

export function PricingCalculator(props: PricingCalculatorProps) {
  return <PricingCalculatorBody {...props} />;
}

function PricingCalculatorBody({
  locale,
  defaultCurrency,
  defaultStructure,
  embedded = false,
  className,
}: PricingCalculatorProps) {
  const t = useTranslations("pages.pricing.calculator");
  const { state, update } = useCalculatorState(
    defaultCurrency,
    defaultStructure
  );
  const posthog = usePostHog();
  const summaryRef = React.useRef<HTMLDivElement>(null);

  const result = React.useMemo(() => (state ? simulate(state) : null), [state]);
  const uncapped = React.useMemo(
    () =>
      state && state.structure === "cash-advance" && state.capEnabled
        ? simulate(state, true)
        : null,
    [state]
  );

  const feePerMonth =
    state && result
      ? feePerMonthPercent(
          state.feePercent,
          result,
          state.structure,
          state.termMonths
        )
      : null;
  const months =
    state && result
      ? monthsToRepay(result, state.structure, state.termMonths)
      : null;

  // One event after the visitor pauses. Derived, rounded figures only:
  // raw money is intentionally never sent.
  React.useEffect(() => {
    if (!state || !result) return;
    const timer = window.setTimeout(() => {
      posthog?.capture("pricing_calculator_calculated", {
        structure: state.structure,
        frequency: state.frequency,
        currency: state.currency,
        feePct: Math.round(state.feePercent * 10) / 10,
        termMonths: state.structure === "term-loan" ? state.termMonths : null,
        sharePct:
          state.structure === "cash-advance" ? state.revenueSharePercent : null,
        capEnabled:
          state.structure === "cash-advance" ? state.capEnabled : null,
        seasonalityMonths: Object.keys(state.revenueOverrides).length,
        complete: result.complete,
        monthsToRepay: months === null ? null : Math.round(months * 10) / 10,
        feePerMonthPct:
          feePerMonth === null ? null : Math.round(feePerMonth * 100) / 100,
      });
    }, ANALYTICS_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [posthog, state, result, months, feePerMonth]);

  return (
    <CalculatorProvider
      locale={locale}
      currency={state?.currency ?? defaultCurrency}
    >
      <div
        className={cn(
          // The results column stretches to the row height and its chart panel
          // absorbs spare space, so when the inputs run longer (Cash Advance)
          // the two panels' bottoms meet. When the results run longer (Term
          // Loan) the inputs panel keeps its natural height.
          "grid grid-cols-1 gap-4",
          !embedded && "lg:grid-cols-12 lg:gap-x-6 lg:gap-y-4",
          className
        )}
        data-testid="pricing-calculator"
      >
        <div className={cn(!embedded && "lg:col-span-5")}>
          {state && result ? (
            <InputsPanel
              state={state}
              months={result.months}
              onChange={update}
            />
          ) : (
            <SkeletonCard
              className="min-h-[520px]"
              label={t("results.loading")}
            />
          )}
        </div>

        <div className={cn("flex flex-col", !embedded && "lg:col-span-7")}>
          {state && result ? (
            <>
              {/* Results read as one stacked white panel: each card collapses
                its top border into the one above, like the redesign's
                stacked bordered frames. */}
              <div ref={summaryRef}>
                <ResultsSummary
                  state={state}
                  result={result}
                  feePerMonth={feePerMonth}
                  months={months}
                  connectors="top"
                />
              </div>
              {uncapped && (
                <CapComparison
                  state={state}
                  withCap={result}
                  withoutCap={uncapped}
                  stack
                />
              )}
              <BalanceChart
                primary={result}
                comparison={uncapped}
                startDate={state.startDate}
                stack
                connectors="bottom"
                className="lg:flex-1"
              />
              {!embedded && (
                <MobileSummaryBar total={result.total} watchRef={summaryRef} />
              )}
            </>
          ) : (
            <>
              <SkeletonCard
                className="min-h-[320px]"
                label={t("results.loading")}
              />
              <SkeletonCard className="-mt-px min-h-[360px] flex-1 border-t-0" />
            </>
          )}
        </div>

        {state && result && (
          <p
            className={cn(
              "text-ds-body-xs text-grey-500 px-1 italic",
              !embedded && "lg:col-span-7 lg:col-start-6"
            )}
          >
            {t("disclaimer")}
          </p>
        )}
      </div>
    </CalculatorProvider>
  );
}

function SkeletonCard({
  className,
  label,
}: {
  className?: string;
  label?: string;
}) {
  return (
    <div
      role={label ? "status" : undefined}
      aria-label={label}
      className={cn(
        "border-grey-100 bg-grey-0 border p-7 motion-safe:animate-pulse",
        className
      )}
    >
      <div className="bg-grey-100 rounded-ds-s h-4 w-32" />
      <div className="bg-grey-100 rounded-ds-s mt-5 h-12 w-2/3" />
      <div className="bg-grey-100 rounded-ds-s mt-4 h-4 w-1/2" />
    </div>
  );
}
